import { useState, useEffect, useRef } from 'react';
import Offcanvas from 'bootstrap/js/dist/offcanvas';
import { Images } from '../Common/Images.jsx';
import { NotificationList_api, NotificationToggleStatus_api, MarkAsReadNotification_api, BookingConfirmation_api, ScheduledBookingConfirmation_api } from '../Common/CommonAPI.jsx';
import { formatDateToEEEddMMM, delay } from "../../utils/CommonUtils.js"
import ErrorPopupModal from './ErrorPopupModal.jsx';
import SuccessPopupModal from './SuccessPopupModal.jsx';
import { useTranslation } from "react-i18next";
import { useApiUiResponse } from "../../hooks/apiUiResponseHelper.js";
// initialFetchEnabled: lets a screen delay the mount-time NotificationList_api call
// until its own boot sequence has finished (the Calendar screen requires
// ZoneList -> Calendar -> Notification). Defaults to true, so screens that pass
// nothing keep fetching on mount as before.
const NotificationPanel = ({ onUnreadChange, onNotificationToggleChange, initialFetchEnabled = true }) => {
    const { t } = useTranslation("common");
    const { handleApiUiResponse, renderApiUi } = useApiUiResponse(t);
    const offcanvasRef = useRef(null);
    const closeButtonRef = useRef(null);
    const isFetchingNotificationsRef = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const [allowNotifications, setAllowNotifications] = useState(0);
    const [expanded, setExpanded] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);

    const [showDetails, setShowDetails] = useState(false);
    const [, setActiveNotification] = useState(null);
    const [notificationDetail, setNotificationDetail] = useState(null);

    const [bookingToggleMap, setBookingToggleMap] = useState({});

    const [errorModalData, setErrorModalData] = useState({
        title: "",
        message: "",
    });
    const [, setSuccessModalData] = useState({
        title: "",
        message: "",
    });
    const [showErrorPopupModal, setShowErrorPopupModal] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(null);

    const hideNotificationPanel = () => {
        const panelElement = offcanvasRef.current;

        if (!panelElement) return;

        try {
            Offcanvas.getOrCreateInstance(panelElement).hide();
            return;
        } catch (error) {
            // Fall back to DOM cleanup in non-Bootstrap test environments.
        }

        if (closeButtonRef.current) {
            closeButtonRef.current.click();
        }

        panelElement.classList.remove("show");
        panelElement.setAttribute("aria-hidden", "true");
        panelElement.removeAttribute("aria-modal");
        panelElement.style.visibility = "hidden";
    };

    const resetValues = () => {
        setIsLoading(false);
        setNotifications([]);
        setActiveNotification(null);
        setNotificationDetail(null);
        setBookingToggleMap({});
    }

    const handleNotificationToggle = async (checked) => {
        const newStatus = checked ? 1 : 0; // boolean → number

        // Optimistic UI update
        setAllowNotifications(checked);
        onNotificationToggleChange?.(checked);

        const requestParams = {
            NotificationStatus: newStatus,
        };

        try {
            setLoading(true);
            await NotificationToggleStatus_api(requestParams);
            console.log("Notification toggle success-newStatus-", newStatus);
        } catch (error) {
            console.error("Notification toggle failed", error);

            setAllowNotifications(!checked);
            onNotificationToggleChange?.(!checked);
        } finally {
            setLoading(false);
        }
    };

    /* const [notification, setNotification] = useState(
     localStorage.getItem("Notifications")
 ); */

    useEffect(() => {
        // Held back while the host screen is still loading its own data; runs once it
        // flips to true (the ref guard inside fetchNotifications prevents duplicates).
        if (!initialFetchEnabled) return;

        fetchNotifications();
        /* if (notification != null) {
           console.log("Notification from sessionStorage in useEffect:", notification);
           const notificationValue = notification === "true" ? 1 : 0;
       handleNotificationToggle(notification);} */
    }, [initialFetchEnabled]);

    useEffect(() => {
        const panelElement = offcanvasRef.current;

        if (!panelElement) return undefined;

        const handlePanelOpen = () => {
            fetchNotifications();
        };

        panelElement.addEventListener("show.bs.offcanvas", handlePanelOpen);

        return () => {
            panelElement.removeEventListener("show.bs.offcanvas", handlePanelOpen);
        };
    }, []);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            const panelElement = offcanvasRef.current;

            if (!panelElement || !panelElement.classList.contains("show")) {
                return;
            }

            if (panelElement.contains(event.target)) {
                return;
            }

            hideNotificationPanel();
        };

        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
        };
    }, []);

    const fetchNotifications = async () => {
        if (isFetchingNotificationsRef.current) {
            return;
        }

        try {
            isFetchingNotificationsRef.current = true;
            setLoading(true);

            const response = await NotificationList_api({});

            const records =
                response?.Data?.Records?.NotificationList ?? [];

            // Map API → UI structure (NO design change)
            const mappedNotifications = records.map((item) => ({
                id: item.Id,
                title: item.Title,
                date: item.Date,
                time: item.Time,
                details: item.Content,
                icon: item.Icon,
                status: item.Status,
            }));

            setNotifications(mappedNotifications);
            // check unread
            const hasUnread = mappedNotifications.some(n => n.status === 0);

            if (onUnreadChange) {
                onUnreadChange(hasUnread);
            }
            // toggle status from API
            const toggleStatus =
                response?.Data?.Records?.NotificationToggleStatus === 1;

            setAllowNotifications(toggleStatus);
            onNotificationToggleChange?.(toggleStatus);
        } catch (error) {
            //console.error("Error fetching notifications", error);
        } finally {
            isFetchingNotificationsRef.current = false;
            setLoading(false);
        }
    };
    // if NotificationId = ClearAll then it will clear all notifications and if send NotificationId= intVal then it will pull specific notification details
    async function fetchNotificationDetail(NotificationId) {
        try {
            setLoading(true);
            const reqParams = { NotificationId: NotificationId }
            const response = await MarkAsReadNotification_api(reqParams);

            if (response && response.Data?.ResponseCode === 200) {
                console.log("MarkAsReadNotification_api called successfully for NotificationId", NotificationId);

                if (NotificationId != "ClearAll")
                    setNotificationDetail(response.Data?.Records);
            } else {
                console.error("MarkAsReadNotification_api failed:", response?.Data?.ResponseMessage);
            }
        } catch (error) {
            console.error("Error for fetchNotificationDetail", error);
        } finally {
            setLoading(false);
        }
    };
    // Require & Release button Click handler
    async function handleRequireReleaseAction(notificationId, uniqueCode, responseFlag) {
        console.log(
            `RequireReleaseHandler: notificationId=${notificationId}, uniqueCode=${uniqueCode}, responseFlag=${responseFlag}`
        );
        try {
            setIsLoading(true);
            
            const reqParams = {
                NotificationId: notificationId,
                UniqueCode: uniqueCode,
                ResponseFlag: responseFlag, // if Required button click then 1, if Realese button click then 0
            };

            const response = await BookingConfirmation_api(reqParams);
            console.log("BookingConfirmation_api response:", response);
            handleApiUiResponse(response, {
                    successToaster: 1,
                    errorToaster: 1,
                    alertPopup: 1
                });
            if (response && response.Data?.ResponseCode === 200) {
                await delay(2000);
                await fetchNotificationDetail(notificationId);
            } else {
                console.error("BookingConfirmation_api failed:", response?.Data?.ResponseMessage);
            }
        } catch (error) {
            console.error("Error calling BookingConfirmation_api:", error);
        } finally {
            setIsLoading(false);
        }
    }
    // To handle Confirm button action with Toggles on/off
    async function handleCancelConfirmAction(
        notificationId,
        uniqueCode,
        zoneId,
        confirmYes,
        confirmNo
    ) {
        try {
            setIsLoading(true);

            const reqParams = {
                NotificationId: notificationId,
                UniqueCode: uniqueCode,
                ZoneId: zoneId,
                ConfirmYes: confirmYes,
                ConfirmNo: confirmNo,
            };

            const response = await ScheduledBookingConfirmation_api(reqParams);

            if (response?.Data?.ResponseCode === 200) {
                await delay(2000);
                await fetchNotificationDetail(notificationId);
                setSuccessModalData({
                    title: "Success!",
                    message:
                        response?.Data?.Message ||
                        "Your response was submitted successfully",
                })
                setShowSuccessModal(true);
            } else {

                setErrorModalData({
                    title: "Opps! An error occurred",
                    message:
                        response?.Data?.ErrorMessage ||
                        response?.Data?.Message ||
                        "Something went wrong",
                });

                setShowErrorPopupModal(true);
            }
        } catch (error) {
            setErrorModalData({
                title: "Unexpected Error",
                message: "Please try again later.",
            });

            setShowErrorPopupModal(true);
        } finally {
            setIsLoading(false);
        }
    }


    const handleConfirmClick = (e) => {
        e.preventDefault();
        if (isLoading) return;

        const confirmYes = [];
        const confirmNo = [];

        Object.entries(bookingToggleMap).forEach(([id, isOn]) => {
            if (isOn) {
                confirmYes.push(id);
            } else {
                confirmNo.push(id);
            }
        });

        handleCancelConfirmAction(
            notificationDetail.Id,
            notificationDetail.UniqueCode,
            notificationDetail.ZoneId,
            confirmYes.join(","),   // eg "12369,12371"
            confirmNo.join(",")     // eg "12370"
        );
    };

    // Logic to set toggles and Buttons-Pair

    let showRealeseRequiredButtonPair = false;
    let showCancelConfirmButtonPair = false;
    let disableRealeseRequiredButtonPair = true;

    if (notificationDetail) {
        if (notificationDetail?.ActionType === 1) {

            if (notificationDetail?.ButtonDisplayFlag === 1) {
                showRealeseRequiredButtonPair = true;
                showCancelConfirmButtonPair = false;

                if (notificationDetail?.ConfirmationFlag === "0") {
                    disableRealeseRequiredButtonPair = true;
                }
                else if (notificationDetail?.ConfirmationFlag === "1") {
                    disableRealeseRequiredButtonPair = true;
                }
                else {
                    disableRealeseRequiredButtonPair = false;
                }
            }
            else {
                showRealeseRequiredButtonPair = false;
                showCancelConfirmButtonPair = true;
                if (notificationDetail?.NotificationBookingList.length) {
                    console.log("notificationDetail?.NotificationBookingList.length - ", notificationDetail?.NotificationBookingList.length);
                }
            }
        }
    }
    useEffect(() => {
        if (!notificationDetail?.NotificationBookingList) return;

        const initialToggleMap = {};

        notificationDetail.NotificationBookingList.forEach((booking) => {
            initialToggleMap[booking.ConfirmationPrimaryId] =
                booking.Released === 0; // ✅ checked when released = 0
        });

        setBookingToggleMap(initialToggleMap);
    }, [notificationDetail]);

    const handleBackClick = () => {
        setShowDetails(false);
        resetValues();
        fetchNotifications();
    };

    return (
        <>
            <div ref={offcanvasRef} className="offcanvas offcanvas-end notification-panel" tabIndex="-1" id="notification-panel" aria-labelledby="notification-panelLabel" data-bs-backdrop="false">
                <div className="offcanvas-body">
                    <div className="notification-list">
                        <div className="accordion" id="notification-info">
                            <div id="listView" style={{ display: showDetails ? "none" : "block" }}>
                                <div className="offcanvas-header">
                                    <h5 id="notification-panelLabel">{t("notifications")}</h5>
                                    <button ref={closeButtonRef} type="button" className="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close">
                                        <img src={Images.Close} alt="" />
                                    </button>
                                </div>
                                <div className="notify-footer border-0">
                                    <div className="powerline d-flex align-items-center gap-3">
                                        <span>{t("react_allow_notifications")}</span>
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="remind-email"
                                                checked={allowNotifications}
                                                onChange={(e) => handleNotificationToggle(e.target.checked)}
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>
                                    <div className="view-link">
                                        <a href="#"
                                            onClick={async (e) => {
                                                e.preventDefault();
                                                if (!isLoading) {
                                                    fetchNotificationDetail("ClearAll");
                                                    fetchNotifications();
                                                }
                                            }}>{t("react_clear_all")}</a>
                                    </div>
                                </div>
                                <div className="accordion-wrap">
                                    {notifications.map((notification) => (

                                        <div className="accordion-item" key={notification.id}>
                                            <div className="accordion-header">
                                                <div
                                                    className="accordion-button collapsed"
                                                    onClick={() => {
                                                        setActiveNotification(notification);
                                                        fetchNotificationDetail(notification.id);
                                                        setShowDetails(true);
                                                    }}
                                                >
                                                    <ul className="notify-details">
                                                        <li>
                                                            <span>
                                                                <img
                                                                    src={notification.icon || Images.logo}
                                                                    alt=""
                                                                    className="img-fluid"
                                                                />
                                                            </span>
                                                            <div className="notify-data">
                                                                <div className="notify-content">
                                                                    <h3>{notification.title}</h3>
                                                                    <p>
                                                                        {formatDateToEEEddMMM(notification.date)} |{" "}
                                                                        {notification.time}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </li>
                                                    </ul>
                                                    {notification.status === 0 && (
                                                        <span className="notify-dots"></span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                    ))}
                                </div>
                            </div>

                            <div id="detailsView" style={{ display: showDetails ? "block" : "none" }}>
                                {/* Header */}
                                <div className="offcanvas-header gap-3">
                                    <a
                                        className="notification-back"
                                        onClick={handleBackClick}
                                    /* onClick={() => {
                                        fetchNotifications();
                                        setShowDetails(false);
                                        setActiveNotification(null);
                                        resetValues();
                                    }} */
                                    >
                                        <img src={Images.BackArrow} className="img-fluid" alt="Back" />
                                    </a>

                                    <h5>{t("notifications")}</h5>

                                    <button
                                        type="button"
                                        className="btn-close text-reset"
                                        data-bs-dismiss="offcanvas"
                                        aria-label="Close"
                                    >
                                        <img src={Images.Close} alt="" />
                                    </button>
                                </div>
                                <div className="accordion-wrap">
                                    {/* DETAILS CONTENT */}
                                    {/*{activeNotification && (*/}
                                    {notificationDetail && (

                                        <div className="accordion-item">
                                            <div className="accordion-header">
                                                <div className="accordion-button">
                                                    <ul className="notify-details">
                                                        <li>
                                                            <span>
                                                                <img
                                                                    src={notificationDetail?.Icon || Images.logo}
                                                                    alt=""
                                                                    className="img-fluid"
                                                                />
                                                            </span>
                                                            <div className="notify-data">
                                                                <div className="notify-content">
                                                                    <h3>{notificationDetail?.Title}</h3>
                                                                    <p>
                                                                        {formatDateToEEEddMMM(notificationDetail?.Date)} |{" "}
                                                                        {notificationDetail?.Time}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>

                                            {/* 👇 YOUR COLLAPSE CONTENT MOVED HERE */}
                                            <div className="accordion-body">
                                                <p style={{ whiteSpace: "pre-line" }}>{notificationDetail?.Content}</p>
                                                {notificationDetail?.NotificationBookingList?.length > 0 && (
                                                    <>
                                                        {notificationDetail?.ZoneName && (
                                                            <div className="content-black">
                                                                <p>{t("zone")}: {notificationDetail.ZoneName}</p>
                                                            </div>
                                                        )}


                                                        <div className="powerline-wrap">

                                                            {notificationDetail.NotificationBookingList.map((booking) => (

                                                                <div className="powerline d-flex justify-content-between gap-3 pe-4" key={booking.ConfirmationPrimaryId}>
                                                                    <div className="powerline-text">
                                                                         <span>{t("spot")}: {booking.SpotName}</span>
                                                                        <p className="p-0">{formatDateToEEEddMMM(booking.BookingDate)}</p>
                                                                    </div>

                                                                    <div className="form-check form-switch">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            checked={
                                                                                bookingToggleMap[booking.ConfirmationPrimaryId] ?? false
                                                                            }
                                                                            disabled={
                                                                                booking.ConfirmationFlag !== "0"
                                                                            }
                                                                            onChange={(e) =>
                                                                                setBookingToggleMap((prev) => ({
                                                                                    ...prev,
                                                                                    [booking.ConfirmationPrimaryId]: e.target.checked,
                                                                                }))
                                                                            }
                                                                        />
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>


                                                    </>
                                                )}
                                                {showRealeseRequiredButtonPair && (
                                                    <div className="d-flex accordion-btn gap-4 justify-content-center">
                                                        <a className={`main-btn outline-gray-btn ${disableRealeseRequiredButtonPair ? "disable" : ""}`}
                                                            href="#"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                if (isLoading) return;
                                                                if (disableRealeseRequiredButtonPair) return;
                                                                handleRequireReleaseAction(notificationDetail?.Id, notificationDetail?.UniqueCode, 0)
                                                            }}>
                                                            {t("release")}
                                                        </a>
                                                        <a className={`main-btn fill-green-btn ${disableRealeseRequiredButtonPair ? "green-disabled" : ""}`}
                                                            href="#"
                                                            onClick={(e) => {
                                                                if (isLoading) return;
                                                                if (disableRealeseRequiredButtonPair) { console.log("disableRealeseRequiredButtonPair"); return; };
                                                                console.log("disableRealeseRequiredButtonPair status-", disableRealeseRequiredButtonPair)
                                                                e.preventDefault();
                                                                handleRequireReleaseAction(notificationDetail?.Id, notificationDetail?.UniqueCode, 1)
                                                            }}
                                                        >
                                                            {t("react_required")}
                                                        </a>
                                                    </div>
                                                )}

                                                {showCancelConfirmButtonPair && (
                                                    <div className="d-flex accordion-btn gap-4 justify-content-center">
                                                        <a className="main-btn outline-gray-btn"
                                                            href="#"
                                                            onClick={handleBackClick}
                                                        >
                                                            {t("cancel")}
                                                        </a>

                                                        <a
                                                            className="main-btn fill-green-btn"
                                                            href="#"
                                                            onClick={handleConfirmClick}
                                                        >
                                                            {t("confirm")}
                                                        </a>
                                                    </div>
                                                )}

                                            </div>
                                        </div>

                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {showErrorPopupModal && (

                <ErrorPopupModal
                    title={errorModalData.title}
                    message={errorModalData.message}
                    onClose={() => setShowErrorPopupModal(false)}
                />
            )

            }
            {showSuccessModal && (
                <SuccessPopupModal
                    title="Success"
                    message="Your response was submitted successfully"
                    onClose={() => setShowSuccessModal(false)}
                />
            )}
            {renderApiUi(isLoading)}
        </>
    );

};

export default NotificationPanel;
